import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';

/**
 * GextorPro Custom Preset
 *
 * Este preset customiza o tema Aura do PrimeNG para utilizar as cores
 * primárias definidas no sistema de design do GextorPro.
 *
 * As cores são baseadas na paleta $primary-colors do arquivo _variables.scss:
 * - 50: #edf4ff (mais claro)
 * - 500: #587bfc (cor principal)
 * - 950: #171d4f (mais escuro)
 *
 * @see src/app/styles/abstract/_variables.scss
 */
export const GextorPreset = definePreset(Aura, {
  primitive: {
    /**
     * Definição da paleta de cores azul customizada do GextorPro
     * Esta paleta substitui as cores primitivas padrão do Aura
     */
    gextorBlue: {
      50: '#edf4ff',
      100: '#deecff',
      200: '#c3daff',
      300: '#9ec2ff',
      400: '#789eff',
      500: '#587bfc',
      600: '#2943f0',
      700: '#2d3fd5',
      800: '#2738ac',
      900: '#283787',
      950: '#171d4f'
    },
    /**
     * Escala Emerald oficial do GextorPro para estados e ações positivas/confirmação
     */
    emerald: {
      50: '#ecfdf5',
      100: '#d0fae5',
      200: '#a4f4cf',
      300: '#5ee9b5',
      400: '#00d492',
      500: '#00bc7d',
      600: '#009966',
      700: '#007a55',
      800: '#006045',
      900: '#004f3b',
      950: '#002c22'
    },
    /**
     * Remapeia primitivas 'green' do Aura para a escala Emerald do GextorPro,
     * garantindo que nenhum componente nativo utilize verde saturado/lime.
     */
    green: {
      50: '#ecfdf5',
      100: '#d0fae5',
      200: '#a4f4cf',
      300: '#5ee9b5',
      400: '#00d492',
      500: '#00bc7d',
      600: '#009966',
      700: '#007a55',
      800: '#006045',
      900: '#004f3b',
      950: '#002c22'
    }
  },
  semantic: {
    /**
     * Configuração da cor primária
     * Mapeia a paleta gextorBlue como cor primária do sistema
     */
    primary: {
      50: '{gextorBlue.50}',
      100: '{gextorBlue.100}',
      200: '{gextorBlue.200}',
      300: '{gextorBlue.300}',
      400: '{gextorBlue.400}',
      500: '{gextorBlue.500}',
      600: '{gextorBlue.600}',
      700: '{gextorBlue.700}',
      800: '{gextorBlue.800}',
      900: '{gextorBlue.900}',
      950: '{gextorBlue.950}'
    },
    /**
     * Configuração das cores de superfície para light/dark mode
     * Usa a paleta slate do projeto para consistência visual
     */
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '#f8fafc',
          100: '#f1f5f9',
          200: '#e2e8f0',
          300: '#cad5e2',
          400: '#90a1b9',
          500: '#62748e',
          600: '#45556c',
          700: '#314158',
          800: '#1d293d',
          900: '#0f172b',
          950: '#020618'
        }
      },
      dark: {
        surface: {
          0: '#ffffff',
          50: '#f8fafc',
          100: '#f1f5f9',
          200: '#e2e8f0',
          300: '#cad5e2',
          400: '#90a1b9',
          500: '#62748e',
          600: '#45556c',
          700: '#314158',
          800: '#1d293d',
          900: '#0f172b',
          950: '#020618'
        }
      }
    }
  },
  components: {
    button: {
      colorScheme: {
        light: {
          root: {
            success: {
              background: '{emerald.600}',
              hoverBackground: '{emerald.700}',
              activeBackground: '{emerald.800}',
              borderColor: '{emerald.600}',
              hoverBorderColor: '{emerald.700}',
              activeBorderColor: '{emerald.800}',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: '{emerald.600}',
                shadow: 'none'
              }
            }
          },
          outlined: {
            success: {
              hoverBackground: '{emerald.50}',
              activeBackground: '{emerald.100}',
              borderColor: '{emerald.600}',
              color: '{emerald.700}'
            }
          },
          text: {
            success: {
              hoverBackground: '{emerald.50}',
              activeBackground: '{emerald.100}',
              color: '{emerald.700}'
            }
          }
        }
      }
    },
    toast: {
      colorScheme: {
        light: {
          success: {
            background: 'color-mix(in srgb, {emerald.50}, transparent 5%)',
            borderColor: '{emerald.200}',
            color: '{emerald.700}',
            detailColor: '{surface.700}',
            shadow: '0px 4px 8px 0px color-mix(in srgb, {emerald.500}, transparent 96%)',
            closeButton: {
              hoverBackground: '{emerald.100}',
              focusRing: { color: '{emerald.700}', shadow: 'none' }
            }
          }
        }
      }
    },
    tag: {
      root: {
        roundedBorderRadius: '9999px'
      },
      colorScheme: {
        light: {
          success: {
            background: '{emerald.50}',
            color: '{emerald.700}'
          }
        }
      }
    },
    message: {
      colorScheme: {
        light: {
          success: {
            background: 'color-mix(in srgb, {emerald.50}, transparent 5%)',
            borderColor: '{emerald.200}',
            color: '{emerald.700}',
            shadow: '0px 4px 8px 0px color-mix(in srgb, {emerald.500}, transparent 96%)'
          }
        }
      }
    }
  }
});
